/**
 * mSTEM quick-save handler (PRJ-03 / LLD UC 08).
 *
 * Encapsulates the "save current workspace to localStorage" flow used by:
 *   1. The Wave 5 menu-bar Ctrl+S keyboard shortcut (Plan 03-09 will wire).
 *   2. The SaveProjectModal confirmation step for first-save callers that
 *      want to reuse the same quota + serialization guard rails.
 *
 * Returns a status string so the caller can branch:
 *   - 'SAVED'      : Quick-save succeeded for an existing entry (currentId
 *                    pointed to a valid entry in myProjects.list).
 *   - 'NEED_MODAL' : No currentId OR the currentId entry no longer exists
 *                    in the list — caller should open SaveProjectModal.
 *   - 'TOO_LARGE'  : vm.toJSON() returned a string > MAX_VM_STATE_BYTES.
 *                    A 'projectTooLarge' alert is dispatched; save aborted.
 *   - 'QUOTA_FULL' : localStorage write rejected (quota exceeded). A
 *                    'localStorageFull' alert is dispatched; save aborted.
 *   - 'ERROR'      : Unexpected exception (vm.toJSON throw, etc.).
 *
 * Security note (T-03-04-01 / T-03-04-02):
 *   - 500-KB cap on vmState surfaces quota issues BEFORE the localStorage
 *     write attempt — mitigates DoS via oversized JSON.
 *   - Quota errors do NOT crash; they surface via the alerts toast queue.
 */

import {
    writeMyProjects,
    updateMyProject,
    getMyProjectById,
    VM_STATE_MAX_BYTES
} from './mstem-project-storage';
import {updateProjectEntry} from '../reducers/my-projects';
import {showAlertWithTimeout} from '../reducers/alerts';
import log from './log';

// Re-export for callers (e.g., menu-bar Ctrl+S handler in Wave 5) so they
// can read the cap without depending on mstem-project-storage directly.
const MAX_VM_STATE_BYTES = VM_STATE_MAX_BYTES;

const performSave = function (deps) {
    const vm = deps && deps.vm;
    const getState = deps && deps.getState;
    const dispatch = deps && deps.dispatch;
    try {
        const state = getState();
        const slice = state && state.scratchGui && state.scratchGui.myProjects;
        const currentId = slice && slice.currentId;
        if (!currentId) {
            return {status: 'NEED_MODAL'};
        }

        const existing = getMyProjectById(slice.list, currentId);
        if (!existing) {
            return {status: 'NEED_MODAL'};
        }

        const vmState = vm.toJSON();
        if (typeof vmState === 'string' && vmState.length > MAX_VM_STATE_BYTES) {
            showAlertWithTimeout(dispatch, 'projectTooLarge');
            return {status: 'TOO_LARGE'};
        }

        const updated = updateMyProject(existing, {vmState: vmState});
        const nextList = slice.list.map(p => (p.id === currentId ? updated : p));

        let quotaHit = false;
        const ok = writeMyProjects(nextList, () => {
            quotaHit = true;
        });
        if (!ok || quotaHit) {
            showAlertWithTimeout(dispatch, 'localStorageFull');
            return {status: 'QUOTA_FULL'};
        }

        dispatch(updateProjectEntry(updated));
        showAlertWithTimeout(dispatch, 'projectSaved');
        return {status: 'SAVED'};
    } catch (e) {
        log.warn('performSave error', e);
        return {status: 'ERROR'};
    }
};

export {
    performSave,
    MAX_VM_STATE_BYTES
};
